// components/LiquidacionesView.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Icons } from '../utils/icons';
import { authService } from '../services/authService';
import { useTecnicoColores } from '../hooks/useTecnicos';
import {
  MARCAS_VALIDAS, esMarcaAriston, esMarcaValida, normalizarNumeroSerie, esPuestaEnMarcha,
  validarOrdenRapida, validarAsset, preValidarOrdenAriston,
} from '../utils/validacionAriston';

// ─── Constantes de negocio ─────────────────────────────────────────────────────
const MARCA_CODES = {
  'ARISTON': 1, 'FLECK': 2, 'CHAFFOTEAUX': 3,
  'NEUTROS': 4, 'CHAFFOTEAUX ECOMMERCE': 5, 'CHROMAGEN': 6
};
const TIPO_ACTIVIDAD_NOMBRES = { '1': 'Instalación', '2': 'Mantenimiento', '5': 'Reparación', '7': 'Diagnóstico' };

const CONFIG = {
  MAX_PETICIONES_POR_MINUTO: 40,
  DELAY_ENTRE_PETICIONES: 1500,
  USAR_CACHE_PERSISTENTE: true,
  MAX_REINTENTOS: 3,
};

// ─── Mapa CODIGO_FABRICANTE → MARCA ───────────────────────────────────────────
// Códigos que Ariston registra bajo NEUTROS (4), no bajo ARISTON (1).
// El código se deriva de los 7 primeros dígitos del número de serie.
// Lista construida a partir de los rechazos "código de fabricante no encontrado".
const CODIGOS_NEUTROS = new Set([
  '3000361','3000440','3000441',
  '1105360',
  '3201343','3201344','3201345','3201359','3201360','3201951','3205928',
  '3528682',
  '3626375','3626376',
  '3700588',
  '4015127','9102018',
]);

// Códigos que Ariston registra bajo FLECK (2)
const CODIGOS_FLECK = new Set([
  '3000362','3000577',
  '3201679',
  '3601616',
  '3626164',
]);

// ─── Tarifas para albaranes Ariston ───────────────────────────────────────────
const RATE_IDS_ALBARAN = {
  BOMBA_CALOR:          15829, // NUOS (aerotermia ACS)
  BOMBA_CALOR_CALEF:    15832, // NIMBUS (aerotermia calefacción)
  CALDERA:              15830,
  SUST_BOMBA_MENOS_150: 21447, // sustitución bomba de calor < 150 L
  SUST_BOMBA_MAS_150:   21448, // sustitución bomba de calor ≥ 150 L
  SUST_CALENT_GAS:      16033, // sustitución calentador gas
  TERMO_MENOS_75:       15776, // sustitución termo < 75 L
  TERMO_MAS_75:         15777, // sustitución termo ≥ 75 L
  // Termos y calentadores gas sin sustitución → tarifa base (null, sin rate-id)
};

// Item-ids de servicios (fijos en StelOrder)
const SERVICIOS_ITEM_IDS = { '1': 29814389, '2': 29814446, '6': 29814464, '7': 29814474 };

const ARISTON_INVOICE_ACCOUNT = 10260750;

// Devuelve el código numérico de MARCA correcto según el CODIGO_FABRICANTE.
// Si el código es NEUTROS → 4, si no → usa el derivado del brand del activo.
const marcaCodeDesde = (codigoFab, marcaCodeActivo) => {
  if (codigoFab && CODIGOS_FLECK.has(codigoFab))   return MARCA_CODES['FLECK'];   // 2
  if (codigoFab && CODIGOS_NEUTROS.has(codigoFab)) return MARCA_CODES['NEUTROS']; // 4
  return marcaCodeActivo || 0;
};

// ─── Helpers de negocio ────────────────────────────────────────────────────────
// Las reglas de validación (marca, nº de serie, líneas...) viven en
// utils/validacionAriston.js — compartidas con el servidor y con el cierre
// de ruta del técnico.
const normalizarMarca = (m) => { if (!m) return null; const u = m.trim().toUpperCase(); if (MARCAS_VALIDAS.includes(u)) return u; if (esMarcaAriston(m)) return 'ARISTON'; return u; };
const corregirCodigoPostal  = (cp) => { if (!cp) return cp; const m = { '03180': '03181', '03163': '30163' }; return m[String(cp).trim()] ?? String(cp).trim(); };

const formatearFecha = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.split('T')[0]);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};

// Tarjeta KPI — mismo patrón que KPICard en MantenimientosView.js: icono en
// caja de color, valor grande, clicable para aplicar el filtro asociado.
function KPICard({ icon, label, value, sub, colorBg, colorIcon, alert, onClick }) {
  return (
    <button onClick={onClick} className={`text-left bg-white rounded-2xl border shadow-sm p-4 hover:shadow-md transition-shadow w-full ${alert ? 'ring-2 ring-red-400' : ''} ${onClick ? 'cursor-pointer' : 'cursor-default'}`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${colorBg}`}>
        <span className={colorIcon}>{icon}</span>
      </div>
      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{label}</p>
      <p className="text-3xl font-bold text-gray-900 leading-none">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </button>
  );
}

// ─── Componente principal ──────────────────────────────────────────────────────
const LiquidacionesView = () => {
  const tecnicoColors = useTecnicoColores();
  const [ordenes, setOrdenes]               = useState([]);
  const [estadisticas, setEstadisticas]     = useState({});
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');
  const [lastUpdated, setLastUpdated]       = useState(null);
  const [rangoActivo, setRangoActivo]       = useState(null);
  const [mostrarTodasAntiguas, setMostrarTodasAntiguas] = useState(false);
  const [antiguedad, setAntiguedad]         = useState({ mas12: 0, mas18: 0, mas24: 0, ocultadas: 0 });

  const [filtros, setFiltros] = useState({
    tecnico: 'todos', periodo: 'todos', fechaInicio: '', fechaFin: '',
    estadoValidacion: 'todos', tipoError: 'todos', busqueda: '', firmada: 'todos',
  });
  const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);
  const [ordenColumna, setOrdenColumna] = useState({ campo: 'fecha', dir: 'desc' });

  const [exportando, setExportando]         = useState(false);
  const [progresoExportacion, setProgresoExportacion]   = useState({ actual: 0, total: 0 });
  const [validacionesExportacion, setValidacionesExportacion] = useState([]);
  const [ordenesPreValidadas, setOrdenesPreValidadas]   = useState([]);

  const [modalClienteAbierto, setModalClienteAbierto]   = useState(false);
  const [ordenSeleccionadaModal, setOrdenSeleccionadaModal] = useState(null);
  const [clientesCache, setClientesCache]   = useState({});
  const [loadingClientes, setLoadingClientes] = useState({});
  const [assetModal, setAssetModal]         = useState(null);
  const [loadingAssetModal, setLoadingAssetModal] = useState(false);
  const [validacionModal, setValidacionModal] = useState(null);
  const [validandoModal, setValidandoModal] = useState(false);

  // Modal de progreso (reemplaza alert/confirm)
  const [liqModal, setLiqModal]             = useState(null);
  const liqModalResolveRef                  = useRef(null);

  const [importarErrores, setImportarErrores] = useState({
    open: false, step: 'upload', filas: [], resultados: [], parseError: '', procesando: false,
  });
  const importarErroresFileRef = useRef(null);

  const assetCacheRef   = useRef({});
  const clienteCacheRef = useRef({});

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const showLiqConfirm = (config) => new Promise(resolve => {
    liqModalResolveRef.current = resolve;
    setLiqModal({ ...config, type: 'confirm' });
  });
  const confirmLiq = () => { liqModalResolveRef.current?.(true);  setLiqModal(null); };
  const cancelLiq  = () => { liqModalResolveRef.current?.(false); setLiqModal(null); };

  // ── Cache ──────────────────────────────────────────────────────────────────
  const cargarCachePersistente = () => {
    if (!CONFIG.USAR_CACHE_PERSISTENTE) return;
    try {
      const a = sessionStorage.getItem('ariston_asset_cache');
      const c = sessionStorage.getItem('ariston_cliente_cache');
      if (a) assetCacheRef.current   = JSON.parse(a);
      if (c) clienteCacheRef.current = JSON.parse(c);
    } catch {}
  };
  const guardarCachePersistente = () => {
    if (!CONFIG.USAR_CACHE_PERSISTENTE) return;
    try {
      sessionStorage.setItem('ariston_asset_cache',   JSON.stringify(assetCacheRef.current));
      sessionStorage.setItem('ariston_cliente_cache', JSON.stringify(clienteCacheRef.current));
    } catch {}
  };
  // Descarta la caché de assets/clientes (en memoria y sessionStorage). Necesario
  // porque un asset corregido en StelOrder después de un pre-validar fallido se
  // seguía sirviendo desde caché — la corrección nunca se veía reflejada.
  const limpiarCacheAssets = () => {
    assetCacheRef.current   = {};
    clienteCacheRef.current = {};
    setClientesCache({});
    try {
      sessionStorage.removeItem('ariston_asset_cache');
      sessionStorage.removeItem('ariston_cliente_cache');
    } catch {}
  };

  const esperarRateLimit = () => new Promise(r => setTimeout(r, CONFIG.DELAY_ENTRE_PETICIONES));

  const obtenerAssetConCache = async (assetPath, reintentos = 3) => {
    if (!assetPath) throw new Error('Asset path no disponible');
    if (assetCacheRef.current[assetPath]) return assetCacheRef.current[assetPath];
    for (let i = 0; i <= reintentos; i++) {
      try {
        await esperarRateLimit();
        const res  = await fetch(`/.netlify/functions/getAsset?assetPath=${encodeURIComponent(assetPath)}`, { headers: { ...authService.authHeader() } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Error desconocido');
        assetCacheRef.current[assetPath] = data.asset;
        guardarCachePersistente();
        return data.asset;
      } catch (err) {
        if (i === reintentos) throw err;
        await new Promise(r => setTimeout(r, 2000));
      }
    }
  };

  const obtenerClienteConCache = async (accountPath, reintentos = 3) => {
    if (!accountPath) throw new Error('Account path no disponible');
    if (clienteCacheRef.current[accountPath]) return clienteCacheRef.current[accountPath];
    for (let i = 0; i <= reintentos; i++) {
      try {
        await esperarRateLimit();
        const res  = await fetch(`/.netlify/functions/getClient?accountPath=${encodeURIComponent(accountPath)}`, { headers: { ...authService.authHeader() } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Error obteniendo cliente');
        clienteCacheRef.current[accountPath] = data.client;
        guardarCachePersistente();
        return data.client;
      } catch (err) {
        if (i === reintentos) throw err;
        await new Promise(r => setTimeout(r, 2000));
      }
    }
  };

  // ── Validación ─────────────────────────────────────────────────────────────
  // La pre-validación de todas las órdenes ya llega hecha del servidor
  // (getAristonLiquidaciones.js, campo `preValidacion`). Esto solo se usa
  // para revalidar UNA orden desde su ficha — getAsset.js tiene, además,
  // fallback a StelOrder en vivo si el activo aún no está en Firebase.
  const preValidarOrdenCompleta = async (orden) => {
    try {
      const asset = orden.assets?.[0]?.path ? await obtenerAssetConCache(orden.assets[0].path) : null;
      return preValidarOrdenAriston(orden, asset);
    } catch (err) {
      return { valida: false, errores: [`Error: ${err.message}`], advertencias: [] };
    }
  };

  // ── Obtener tipo actividad ─────────────────────────────────────────────────
  const obtenerTipoActividad = (lines, privateComments) => {
    const validos = ['1','2','5','7'];
    if (lines?.length > 0) {
      for (const l of lines) {
        if (l['line-type'] === 'ITEM' && !l.deleted && !l['item-deleted'] && validos.includes(l['item-reference'])) return l['item-reference'];
      }
      for (const l of lines) {
        if (l['line-type'] === 'ITEM' && !l.deleted && !l['item-deleted'] && String(l['item-reference']).startsWith('6')) return '2';
      }
    }
    if (esPuestaEnMarcha(privateComments)) return '1';
    return '';
  };

  const obtenerPiezasParaExcel = (lines, tipoActividad) => {
    if (!lines?.length) return [];
    return lines
      .filter(l =>
        l['line-type'] === 'ITEM' &&
        !l.deleted &&
        !l['item-deleted'] &&
        !l['item-reference']?.includes('SER') &&
        l['item-reference'] !== tipoActividad &&
        !/^PRO/i.test(l['item-reference'] || '')
      )
      .map(l => ({ referencia: l['item-reference'], unidades: l.units || 1 }));
  };

  // ── Albaranes Ariston ──────────────────────────────────────────────────────

  const extraerLitros = (model) => {
    const nums = (model || '').match(/\d+/g) || [];
    const caps = nums.map(Number).filter(n => n >= 30 && n <= 500);
    return caps[caps.length - 1] || 0;
  };

  const determinarRateId = (asset, tipoActividad) => {
    const m = (asset?.model || '').toUpperCase();
    const esNuos    = /NUOS/.test(m);
    const esNimbus  = /NIMBUS/.test(m);
    const esCaldera = /CALDERA|EGIS|GENUS|CARES/.test(m);
    const esGas     = !esNuos && !esNimbus && /GAS|FAST|MICROFAST/.test(m);
    const litros    = extraerLitros(m);

    if (tipoActividad === '7') {
      if (esNuos || esNimbus) return litros >= 150 ? RATE_IDS_ALBARAN.SUST_BOMBA_MAS_150 : RATE_IDS_ALBARAN.SUST_BOMBA_MENOS_150;
      if (esGas)              return RATE_IDS_ALBARAN.SUST_CALENT_GAS;
      return litros >= 75     ? RATE_IDS_ALBARAN.TERMO_MAS_75 : RATE_IDS_ALBARAN.TERMO_MENOS_75;
    }
    if (esNuos)    return RATE_IDS_ALBARAN.BOMBA_CALOR;
    if (esNimbus)  return RATE_IDS_ALBARAN.BOMBA_CALOR_CALEF;
    if (esCaldera) return RATE_IDS_ALBARAN.CALDERA;
    return null; // termos, calentadores gas y resto → tarifa base (category rate)
  };

  const parsearCsvErroresAriston = (csvText) => {
    const lineas = csvText.split(/\r?\n/).filter(l => l.trim());
    if (lineas.length < 2) return { error: 'CSV vacío o sin datos' };
    const primera = lineas[0];
    const sep = (primera.split(';').length >= primera.split(',').length) ? ';' : ',';
    const headers = primera.split(sep).map(h => h.trim().replace(/^"|"$/g, '').toUpperCase());
    const colParte = headers.findIndex(h =>
      h.includes('PARTE') || h.includes('COLABORADOR') || h.includes('REFERENCIA')
    );
    const colError = headers.findIndex(h =>
      h.includes('ERROR') || h.includes('DESCRIPCION') || h.includes('DESCRIPCIÓN') || h.includes('MOTIVO') || h.includes('MENSAJE')
    );
    if (colParte === -1) return { error: 'No se encontró columna de referencia del parte (PARTE_COLABORADOR, NUM_PARTE…)' };
    if (colError === -1) return { error: 'No se encontró columna de error (ERROR, DESCRIPCION…)' };

    const filas = [];
    for (let i = 1; i < lineas.length; i++) {
      const cols = lineas[i].split(sep).map(c => c.trim().replace(/^"|"$/g, ''));
      if (cols.length <= Math.max(colParte, colError)) continue;
      const errorMsg = cols[colError] || '';
      if (/ya\s+existe/i.test(errorMsg)) {
        filas.push({ referencia: cols[colParte], errorMsg });
      }
    }
    return { filas };
  };

  // Antes se descartaban aquí TODAS las líneas de servicio (ids 1/2/5/6/7),
  // asumiendo que la línea sintética de servicio (más abajo, por tarifa) ya
  // las cubría. Pero esa línea sintética representa solo UNA (tipoActividad,
  // la ganadora de obtenerTipoActividad) — si la orden trae más de una línea
  // de servicio en StelOrder, las demás se perdían sin facturar. Ahora solo
  // se descarta la que coincide exactamente con tipoActividad (para no
  // duplicar esa); cualquier otra línea de servicio (mismo grupo de ids,
  // pero no la elegida) pasa igual que un material — crearAlbaranAriston.js
  // decide el descuento (0% servicio / 40% material) por su cuenta.
  const construirLineasParaAlbaran = (orden, tipoActividad) => {
    return (orden.lines || [])
      .filter(l =>
        l['line-type'] === 'ITEM' && !l.deleted && !l['item-deleted'] &&
        l['item-reference'] !== tipoActividad &&
        !l['item-reference']?.includes('SER') &&
        !/^PRO/i.test(l['item-reference'] || '')
      )
      .map(l => ({
        itemId: l['item-id'], itemReference: l['item-reference'],
        units: l.units || 1, warehouseId: l['warehouse-id'],
      }));
  };

  const procesarErroresAriston = async () => {
    const filasConOrden = importarErrores.filas.filter(f => f.orden);
    if (!filasConOrden.length) return;

    setImportarErrores(prev => ({ ...prev, step: 'processing', procesando: true, resultados: [] }));
    const resultados = [];

    for (const fila of filasConOrden) {
      const orden = fila.orden;
      let asset = null;
      try {
        if (orden.assets?.[0]?.path) asset = await obtenerAssetConCache(orden.assets[0].path);
      } catch {}

      const tipoActividad = obtenerTipoActividad(orden.lines, orden['private-comments']);
      const rateId  = determinarRateId(asset, tipoActividad);
      const lineas  = construirLineasParaAlbaran(orden, tipoActividad);
      const payload = {
        workOrderId:        orden.id,
        fullReference:      orden['full-reference'],
        accountId:          orden['account-id'],
        workAddressId:      orden['work-address-id'],
        invoicingAddressId: orden['invoicing-address-id'],
        agentId:            orden['agent-id'],
        rateId,
        tipoActividad,
        lineas,
        assets:             orden.assets || [],
        privateComments:    orden['private-comments'] || null,
        comments:           orden.comments || null,
      };

      try {
        const resp = await fetch('/.netlify/functions/crearAlbaranAriston', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({ ordenes: [payload] }),
        });
        const data = await resp.json();
        const r = data.resultados?.[0] || {};
        resultados.push({ referencia: orden['full-reference'], ...r });
      } catch (err) {
        resultados.push({ referencia: orden['full-reference'], workOrderId: orden.id, success: false, error: err.message });
      }

      setImportarErrores(prev => ({ ...prev, resultados: [...resultados] }));
    }

    setImportarErrores(prev => ({ ...prev, step: 'done', procesando: false, resultados }));
  };

  const parsearArchivoErrores = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const result = parsearCsvErroresAriston(text);
      if (result.error) {
        setImportarErrores(prev => ({ ...prev, parseError: result.error, filas: [] }));
        return;
      }
      const filasConOrden = result.filas.map(f => ({
        ...f,
        orden: ordenes.find(o => o['full-reference'] === f.referencia || String(o.id) === f.referencia) || null,
      }));
      setImportarErrores(prev => ({
        ...prev, parseError: '', filas: filasConOrden,
        step: filasConOrden.length > 0 ? 'preview' : 'upload',
      }));
    };
    reader.onerror = () => setImportarErrores(prev => ({ ...prev, parseError: 'Error leyendo el archivo' }));
    reader.readAsText(file, 'windows-1252');
  };

  const parsearDireccionEspanola = (addressData) => {
    const resultado = { nombreCalle: '', numeroCalle: '' };
    if (!addressData) return resultado;
    let dir = addressData.trim().toUpperCase();
    if (!dir || dir === '_' || dir === '-') { resultado.nombreCalle = 'SIN DIRECCION'; return resultado; }
    if (/^\d+\.?\d*$/.test(dir)) { resultado.numeroCalle = dir.replace('.0',''); resultado.nombreCalle = 'SIN NOMBRE'; return resultado; }

    const tipos = ['CALLE','C/','CL','AVENIDA','AVDA','AVDA.','AV','AV.','PASEO','PSO','PLAZA','PZA','PZ','CAMINO','CMN','CARRETERA','CTRA','CTRA.','RONDA','TRAVESIA','URBANIZACION','URB','URBANIZACIÓN','GLORIETA','PASAJE','PARQUE'];
    for (const t of tipos) { const r = new RegExp(`^${t}\\s+`,'i'); if (r.test(dir)) { dir = dir.replace(r,'').trim(); break; } }

    const m = dir.match(/\s+(\d+(?:\s*[-\/]\s*\d+)?(?:\s*[A-Z])?)\s*/);
    if (m) {
      resultado.nombreCalle = dir.substring(0, m.index).trim();
      resultado.numeroCalle = (m[1].match(/\d+/) || [''])[0];
    } else {
      resultado.nombreCalle = dir.trim();
    }

    if (resultado.nombreCalle) {
      resultado.nombreCalle = resultado.nombreCalle.replace(/,\s*$/,'').replace(/\s+/,' ').trim()
        .toLowerCase().split(' ').map((p,i) => {
          const exc = ['de','del','la','las','los','el','y','a'];
          return (i > 0 && exc.includes(p)) ? p : p.charAt(0).toUpperCase() + p.slice(1);
        }).join(' ');
    }
    if (!resultado.nombreCalle && resultado.numeroCalle) resultado.nombreCalle = 'SIN NOMBRE';
    return resultado;
  };

  // ── Exportar a CSV ──────────────────────────────────────────────────────────
  const procesarOrdenParaExcel = async (orden) => {
    try {
      // Reusar resultado de pre-validación si ya está disponible
      const preVal = ordenesPreValidadas.find(r => r.orden.id === orden.id);
      if (preVal && !preVal.valida) throw new Error(preVal.errores[0] || 'Inválida (pre-validación)');

      // Validación rápida (sin API): firma, activos, líneas, tipo actividad
      const erroresRapidos = validarOrdenRapida(orden);
      if (erroresRapidos.length > 0) throw new Error(erroresRapidos.join('; '));

      const asset = await obtenerAssetConCache(orden.assets[0].path);
      const erroresAsset = validarAsset(asset);
      if (erroresAsset.length > 0) throw new Error(erroresAsset.join('; '));

      let nombreCliente = orden['customer-name'] || '';
      let apellidos = '-', codigoPostal = '', localidad = '', nombreCalle = '', numeroCalle = '';

      if (orden['customer-name'] && orden['customer-address']) {
        const parts = orden['customer-address'].split(',');
        if (parts.length >= 3) {
          const parsed = parsearDireccionEspanola(parts[0].trim());
          nombreCalle  = parsed.nombreCalle;
          numeroCalle  = parsed.numeroCalle;
          localidad    = parts[1]?.trim() || '';
          codigoPostal = corregirCodigoPostal(parts[2]?.trim() || '');
        }
      } else if (orden['account-path']) {
        try {
          const cliente   = await obtenerClienteConCache(orden['account-path']);
          const dir       = cliente['main-address'];
          const parsed    = parsearDireccionEspanola(dir?.['address-data']);
          nombreCalle     = parsed.nombreCalle;
          numeroCalle     = parsed.numeroCalle;
          codigoPostal    = corregirCodigoPostal(dir?.['postal-code'] || '');
          localidad       = dir?.['city-town'] || '';
          const legal     = cliente['legal-name'] || cliente.name || '';
          nombreCliente   = cliente.name || '';
          // Anclar la búsqueda al inicio para evitar eliminar ocurrencias internas
          apellidos = nombreCliente
            ? legal.replace(new RegExp(`^${nombreCliente.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`), '').trim() || '-'
            : legal || '-';
        } catch {
          nombreCliente = orden['customer-name'] || 'Sin nombre';
        }
      }

      const tipoActividad   = obtenerTipoActividad(orden.lines, orden['private-comments']);
      const piezas          = obtenerPiezasParaExcel(orden.lines, tipoActividad);
      const serialNorm      = normalizarNumeroSerie(asset.serialNumber);
      const codigoFab       = serialNorm?.substring(0, 7) || '';
      const marcaNorm       = normalizarMarca(asset.brand);
      const marcaCode       = marcaCodeDesde(codigoFab, MARCA_CODES[marcaNorm]);

      // Ariston rechaza si FECHA_REPARACION < FECHA_PARTE.
      // Usamos la fecha más antigua como FECHA_PARTE y la más reciente como FECHA_REPARACION.
      const fechaAsignada   = orden['assigned-date'] || orden.date;
      const fechaReparacion = orden.date;
      const fechaParte      = (fechaAsignada && fechaAsignada <= fechaReparacion)
        ? fechaAsignada
        : fechaReparacion; // si assigned > reparacion, usar reparacion para ambas

      if (!codigoPostal || codigoPostal === '00000') {
        throw new Error('Código postal inválido (00000 o vacío)');
      }

      const row = {
        NOMBRE_CLIENTE: nombreCliente || '-', APELLIDOS: apellidos || '-',
        SIGLA_CALLE: 'CL', NOMBRE_CALLE: nombreCalle || 'SIN NOMBRE',
        NUMERO_CALLE: numeroCalle || '1', DESCRIPTOR: '',
        CODIGO_POSTAL: codigoPostal, LOCALIDAD: localidad || 'SIN LOCALIDAD',
        CODIGO_PAIS: 'ESP', CIF_NIF_CLIENTE: '',
        MARCA: marcaCode,
        CODIGO_FABRICANTE: codigoFab,
        FECHA_PUESTA_MARCHA: formatearFecha(asset.warrantyStartDate) || '',
        FECHA_FUERA_SERVICIO: '', NIF_INSTALADORA: '',
        NUMERO_SERIE: serialNorm || '',
        PARTE_COLABORADOR: orden['full-reference'] || orden.id,
        DEFECTO_DENUNCIADO: (orden['private-comments'] || '').replace(/[\n\r]/g,' ').substring(0,500),
        FECHA_PARTE:      formatearFecha(fechaParte),
        FECHA_REPARACION: formatearFecha(fechaReparacion),
        TIPO_ACTIVIDAD: tipoActividad || '2',
        OBSERVACIONES_TECNICO: (orden.comments || 'N/A').replace(/[\n\r]/g,' ').substring(0,500),
        NOMBRE_TECNICO: orden.tecnicoNombre || '',
        IMPORTE_ESPECIAL: 0, IMPORTE_ESPECIAL_CONCEPTO: '',
      };

      for (let i = 0; i < 10; i++) {
        row[`REFERENCIA${i+1}`] = piezas[i]?.referencia || '';
        row[`UNIDADES${i+1}`]   = piezas[i]?.unidades   || '';
      }

      return { success: true, data: row, ordenId: orden.id, fullReference: orden['full-reference'] || orden.id };
    } catch (err) {
      return { success: false, error: err.message, ordenId: orden.id, fullReference: orden['full-reference'] || orden.id };
    }
  };

  const exportarAExcel = async () => {
    const ordenesAExportar = getOrdenesFiltradas;
    if (ordenesAExportar.length === 0) {
      setLiqModal({ type: 'info', title: 'Sin órdenes', message: 'No hay órdenes para exportar con los filtros aplicados.' });
      return;
    }

    // Contar assets no cacheados (solo esos necesitarán llamada API + delay)
    const preValidadasInvalidasIds = new Set(
      ordenesPreValidadas.filter(r => !r.valida).map(r => r.orden.id)
    );
    const sinCache = ordenesAExportar.filter(o => {
      if (preValidadasInvalidasIds.has(o.id)) return false;
      const path = o.assets?.[0]?.path;
      return path && !assetCacheRef.current[path];
    }).length;
    const skippedCount = ordenesAExportar.filter(o => preValidadasInvalidasIds.has(o.id)).length;
    const mins = Math.ceil((sinCache * CONFIG.DELAY_ENTRE_PETICIONES) / 60000);
    const timeStr = sinCache === 0 ? 'muy rápido (todo en caché)' : `~${mins} minuto${mins !== 1 ? 's' : ''}`;

    const ok = await showLiqConfirm({
      title: 'Exportar a CSV',
      message: `Se procesarán ${ordenesAExportar.length} órdenes${contarFiltrosActivos() > 0 ? ` (${contarFiltrosActivos()} filtros activos)` : ''}.${skippedCount > 0 ? ` ${skippedCount} ya pre-validadas como inválidas serán omitidas.` : ''}`,
      detail: `Tiempo estimado: ${timeStr}. ${sinCache} activo${sinCache !== 1 ? 's' : ''} sin caché necesita${sinCache !== 1 ? 'n' : ''} consulta API.`,
    });
    if (!ok) return;

    setExportando(true);
    setProgresoExportacion({ actual: 0, total: ordenesAExportar.length });
    setValidacionesExportacion([]);
    setLiqModal({ type: 'progress', title: 'Exportando órdenes...', total: ordenesAExportar.length, processed: 0, items: [] });
    cargarCachePersistente();

    const exitosas = []; const erroresTemp = [];
    const inicio = Date.now();

    try {
      for (let i = 0; i < ordenesAExportar.length; i++) {
        const res = await procesarOrdenParaExcel(ordenesAExportar[i]);
        if (res.success) exitosas.push(res.data);
        else erroresTemp.push(res);

        setProgresoExportacion({ actual: i + 1, total: ordenesAExportar.length });
        setLiqModal(prev => prev ? {
          ...prev,
          processed: i + 1,
          items: [...(prev.items || []).slice(-12), {
            name: res.fullReference,
            status: res.success ? 'ok' : 'error',
            detail: res.success ? '' : res.error,
          }],
        } : null);
      }

      setValidacionesExportacion(erroresTemp);

      if (exitosas.length === 0) {
        setLiqModal({ type: 'info', title: 'Sin resultados', message: 'No se pudo procesar ninguna orden correctamente.' });
        return;
      }

      // Generar CSV
      const cols    = Object.keys(exitosas[0]);
      const defecto = { NOMBRE_CLIENTE: '-', NUMERO_CALLE: '1' };
      const csvBody = exitosas.map(fila =>
        cols.map(c => {
          const v = fila[c] ?? defecto[c] ?? '';
          const s = String(v);
          return (s.includes(';') || s.includes('\n') || s.includes('"')) ? `"${s.replace(/"/g,'""')}"` : s;
        }).join(';')
      );
      const csv    = [cols.join(';'), ...csvBody].join('\n');
      const fecha  = new Date().toISOString().split('T')[0].replace(/-/g,'');
      const nombre = `Liquidacion_ARISTON_${fecha}.csv`;
      const blob   = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url    = URL.createObjectURL(blob);
      const a      = document.createElement('a');
      a.href = url; a.download = nombre; a.style.visibility = 'hidden';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);

      const secs = Math.round((Date.now() - inicio) / 1000);
      setLiqModal({
        type: 'done',
        title: 'Exportación completada',
        stats: { exitosas: exitosas.length, errores: erroresTemp.length, secs, nombre },
        items: erroresTemp.slice(0, 15).map(e => ({ name: e.fullReference, status: 'error', detail: e.error })),
        hasErrors: erroresTemp.length > 0,
      });
    } catch (err) {
      setLiqModal({ type: 'info', title: 'Error en exportación', message: err.message });
    } finally {
      setExportando(false);
      setProgresoExportacion({ actual: 0, total: 0 });
    }
  };

  const exportarErroresACSV = () => {
    if (validacionesExportacion.length === 0) return;
    const filas = validacionesExportacion.map(e => `${e.fullReference};${e.error?.replace(/;/g,',') || ''}`);
    const csv   = ['REFERENCIA;ERROR', ...filas].join('\n');
    const fecha = new Date().toISOString().split('T')[0].replace(/-/g,'');
    const blob  = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url   = URL.createObjectURL(blob);
    const a     = document.createElement('a');
    a.href = url; a.download = `Errores_Liquidacion_${fecha}.csv`; a.style.visibility = 'hidden';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ── Cargar órdenes ─────────────────────────────────────────────────────────
  const cargarOrdenes = async (rango) => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (rango?.desde) params.set('desde', rango.desde);
      if (rango?.hasta) params.set('hasta', rango.hasta);
      // Por defecto se ocultan las órdenes de más de 12 meses en preliquidación:
      // casi siempre son liquidaciones antiguas que salieron del estado en
      // StelOrder y su espejo en Firebase nunca se resincronizó (drift).
      if (mostrarTodasAntiguas) params.set('todas', 'true');
      else params.set('maxMeses', '12');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res  = await fetch(`/.netlify/functions/getAristonLiquidaciones${qs}`, { headers: { ...authService.authHeader() } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      const lista = data.ordenes || [];
      setOrdenes(lista);
      setEstadisticas(data.estadisticas || {});
      setAntiguedad({ ...(data.antiguedad || {}), ocultadas: data.ocultadasPorAntiguedad || 0 });
      setLastUpdated(new Date());
      setRangoActivo(data.rangoAplicado || null);
      // Cada recarga (manual, por rango, o al montar) descarta la caché de
      // assets/clientes y la vuelve a llenar con los activos que ya trae la
      // respuesta — así la exportación y la ficha de la orden no los piden
      // otra vez uno a uno, y un activo corregido se ve en cuanto se recarga.
      limpiarCacheAssets();
      for (const o of lista) {
        const path = o.assets?.[0]?.path;
        if (path && o.asset) assetCacheRef.current[path] = o.asset;
      }
      guardarCachePersistente();
      // La pre-validación llega hecha del servidor (ver getAristonLiquidaciones.js).
      setOrdenesPreValidadas(lista.filter(o => o.preValidacion).map(o => ({ orden: o, ...o.preValidacion })));
    } catch (err) {
      setError(`Error cargando órdenes: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Convierte el filtro de período activo en un rango {desde,hasta} (YYYY-MM-DD)
  // para pedirle a StelOrder solo esas órdenes, en vez de las 500 completas.
  const formatoISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const calcularRangoDeFiltro = () => {
    if (filtros.periodo === 'personalizado') {
      return { desde: filtros.fechaInicio || null, hasta: filtros.fechaFin || null };
    }
    const inicio = obtenerFechaInicioPeriodo(filtros.periodo);
    if (!inicio) return null; // 'todos' → sin rango, trae todo
    return { desde: formatoISO(inicio), hasta: formatoISO(new Date()) };
  };

  const cargarPorRangoSeleccionado = () => cargarOrdenes(calcularRangoDeFiltro());

  // Carga automática al montar
  useEffect(() => { cargarOrdenes(calcularRangoDeFiltro()); }, [mostrarTodasAntiguas]); // eslint-disable-line

  // ── Modal de orden ─────────────────────────────────────────────────────────
  const abrirModalCliente = async (orden) => {
    setOrdenSeleccionadaModal(orden);
    setModalClienteAbierto(true);
    setAssetModal(null);
    setValidacionModal(null);

    const accountPath = orden['account-path'];
    if (accountPath && !clientesCache[accountPath] && !loadingClientes[accountPath]) {
      cargarInfoCliente(accountPath);
    }

    const assetPath = orden.assets?.[0]?.path;
    if (assetPath) {
      if (assetCacheRef.current[assetPath]) {
        setAssetModal(assetCacheRef.current[assetPath]);
      } else {
        setLoadingAssetModal(true);
        try {
          const asset = await obtenerAssetConCache(assetPath);
          setAssetModal(asset);
        } catch { setAssetModal({ error: 'No se pudo cargar el activo' }); }
        finally { setLoadingAssetModal(false); }
      }
    }
  };

  const cerrarModalCliente = () => {
    setModalClienteAbierto(false);
    setOrdenSeleccionadaModal(null);
    setAssetModal(null);
    setValidacionModal(null);
  };

  const navegarOrden = async (dir) => {
    if (!ordenSeleccionadaModal) return;
    const lista = getOrdenesOrdenadas;
    const idx   = lista.findIndex(o => o.id === ordenSeleccionadaModal.id);
    const nuevo = dir === 'anterior'
      ? (idx > 0 ? idx - 1 : lista.length - 1)
      : (idx < lista.length - 1 ? idx + 1 : 0);
    const nuevaOrden = lista[nuevo];
    setOrdenSeleccionadaModal(nuevaOrden);
    setAssetModal(null); setValidacionModal(null);

    const ap = nuevaOrden['account-path'];
    if (ap && !clientesCache[ap] && !loadingClientes[ap]) cargarInfoCliente(ap);

    const assetPath = nuevaOrden.assets?.[0]?.path;
    if (assetPath) {
      if (assetCacheRef.current[assetPath]) { setAssetModal(assetCacheRef.current[assetPath]); }
      else {
        setLoadingAssetModal(true);
        try { setAssetModal(await obtenerAssetConCache(assetPath)); }
        catch { setAssetModal({ error: 'No se pudo cargar' }); }
        finally { setLoadingAssetModal(false); }
      }
    }
  };

  const formatearInfoCliente = (c) => {
    const dir = c['main-address'];
    return {
      displayName: c['legal-name'] || c.name || 'Sin nombre',
      nombreCompleto: (c['legal-name'] && c.name && c['legal-name'] !== c.name)
        ? [{ tipo: 'Nombre legal', valor: c['legal-name'] }, { tipo: 'Nombre comercial', valor: c.name }]
        : [],
      contacto: { telefono1: c.phone || '', telefono2: c['alternative-phone'] || '', email: c.email || '' },
      direccionFormateada: dir ? `${dir['address-data'] || ''}, ${dir['city-town'] || ''}, ${dir['postal-code'] || ''}`.trim() : '',
    };
  };

  const cargarInfoCliente = async (accountPath) => {
    if (!accountPath) return;
    setLoadingClientes(prev => ({ ...prev, [accountPath]: true }));
    try {
      const c = await obtenerClienteConCache(accountPath);
      setClientesCache(prev => ({ ...prev, [accountPath]: formatearInfoCliente(c) }));
    } catch (err) {
      setClientesCache(prev => ({ ...prev, [accountPath]: { error: err.message } }));
    } finally {
      setLoadingClientes(prev => ({ ...prev, [accountPath]: false }));
    }
  };

  const validarOrdenIndividual = async () => {
    if (!ordenSeleccionadaModal) return;
    setValidandoModal(true); setValidacionModal(null);
    try {
      // Sin caché para esta orden: si se pulsa "Validar" es porque se acaba
      // de corregir algo y se quiere ver el dato de ahora, no el de la carga.
      const path = ordenSeleccionadaModal.assets?.[0]?.path;
      if (path) delete assetCacheRef.current[path];
      const result = await preValidarOrdenCompleta(ordenSeleccionadaModal);
      setValidacionModal(result);
      if (path && assetCacheRef.current[path]) setAssetModal(assetCacheRef.current[path]);
      setOrdenesPreValidadas(prev => [
        ...prev.filter(r => r.orden.id !== ordenSeleccionadaModal.id),
        { orden: ordenSeleccionadaModal, ...result },
      ]);
    } catch (err) {
      setValidacionModal({ valida: false, errores: [err.message] });
    } finally {
      setValidandoModal(false);
    }
  };

  const getProductosOrden = (orden) => {
    if (!orden.lines?.length) return [];
    return orden.lines
      .filter(l => l['line-type'] === 'ITEM' && !l.deleted && !l['item-deleted'])
      .map(l => ({ id: l.id, name: l['item-name'] || 'Sin nombre', reference: l['item-reference'] || '', quantity: l.units || 1 }));
  };

  // ── Filtros ────────────────────────────────────────────────────────────────
  const obtenerFechaInicioPeriodo = (p) => {
    const hoy = new Date(); hoy.setHours(0,0,0,0);
    if (p === 'hoy') return hoy;
    if (p === 'semana') { const d = new Date(hoy); d.setDate(hoy.getDate() - hoy.getDay() + 1); return d; }
    if (p === 'mes') return new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    if (p === 'ultimo-mes') { const d = new Date(hoy); d.setDate(hoy.getDate() - 30); return d; }
    return null;
  };

  const ordenCumpleFiltroFecha = (orden, f) => {
    const fo = new Date(orden.dateOriginal || orden.date); fo.setHours(0,0,0,0);
    if (f.periodo === 'personalizado') {
      if (f.fechaInicio && fo < new Date(f.fechaInicio)) return false;
      if (f.fechaFin) { const fin = new Date(f.fechaFin); fin.setHours(23,59,59,999); if (fo > fin) return false; }
      return true;
    }
    if (f.periodo === 'todos') return true;
    return fo >= obtenerFechaInicioPeriodo(f.periodo);
  };

  // Clasifica UN mensaje de error concreto en un tipo — antes solo se miraba
  // errores[0] (obtenerTipoErrorPrincipal), así que una orden con "No firmada"
  // + "Sin número de serie" nunca aparecía al filtrar por "Sin nº serie",
  // aunque el problema estuviera ahí. Ahora se clasifica cada error de la
  // lista y una orden puede contar en varios tipos a la vez.
  const clasificarError = (e) => {
    const t = e.toLowerCase();
    if (t.includes('no firmada'))         return 'sin-firma';
    if (t.includes('sin activos'))        return 'sin-activos';
    if (t.includes('sin líneas'))         return 'sin-lineas';
    if (t.includes('tipo de actividad'))  return 'tipo-actividad-invalido';
    if (t.includes('marca'))              return 'marca-invalida';
    if (t.includes('número de serie'))    return 'sin-serie';
    if (t.includes('garantía'))           return 'sin-garantia';
    if (t.includes('asset') || t.includes('error')) return 'error-asset';
    return 'otro';
  };
  // Todos los tipos de error presentes en una validación (no solo el primero).
  const obtenerTiposError = (v) => {
    if (!v || v.valida || !v.errores?.length) return [];
    return [...new Set(v.errores.map(clasificarError))];
  };
  // Compat: tipo "principal" (el primero) — se sigue usando donde solo hace
  // falta un resumen de un vistazo (p. ej. el motivo corto de una tarjeta).
  const obtenerTipoErrorPrincipal = (v) => obtenerTiposError(v)[0] || null;

  const NOMBRES_ERROR = {
    'sin-firma': 'Sin firma', 'sin-activos': 'Sin activos', 'sin-lineas': 'Sin líneas',
    'tipo-actividad-invalido': 'Tipo actividad', 'marca-invalida': 'Marca inválida',
    'sin-serie': 'Sin nº serie', 'sin-garantia': 'Sin garantía', 'error-asset': 'Error asset', 'otro': 'Otro',
  };

  const getOrdenesFiltradas = useMemo(() => {
    let r = [...ordenes];
    if (filtros.tecnico !== 'todos') r = r.filter(o => o.tecnicoNombre === filtros.tecnico);
    r = r.filter(o => ordenCumpleFiltroFecha(o, filtros));
    if (filtros.estadoValidacion !== 'todos' && ordenesPreValidadas.length > 0) {
      if (filtros.estadoValidacion === 'validas')      r = r.filter(o => ordenesPreValidadas.find(p => p.orden.id === o.id)?.valida);
      if (filtros.estadoValidacion === 'invalidas')    r = r.filter(o => { const p = ordenesPreValidadas.find(p => p.orden.id === o.id); return p && !p.valida; });
      if (filtros.estadoValidacion === 'sin-validar')  r = r.filter(o => !ordenesPreValidadas.find(p => p.orden.id === o.id));
    }
    if (filtros.tipoError !== 'todos' && ordenesPreValidadas.length > 0) {
      r = r.filter(o => {
        const v = ordenesPreValidadas.find(p => p.orden.id === o.id);
        return v && obtenerTiposError(v).includes(filtros.tipoError);
      });
    }
    if (filtros.firmada !== 'todos') r = r.filter(o => o.signed === (filtros.firmada === 'si'));
    if (filtros.busqueda.trim()) {
      const q = filtros.busqueda.toLowerCase().trim();
      r = r.filter(o =>
        (o['full-reference'] || '').toLowerCase().includes(q) ||
        (o['customer-name']  || '').toLowerCase().includes(q) ||
        (o['private-comments'] || '').toLowerCase().includes(q)
      );
    }
    return r;
  }, [ordenes, filtros, ordenesPreValidadas]); // eslint-disable-line

  // Orden aplicado sobre lo ya filtrado — para poder agrupar visualmente por
  // técnico o por fecha antes de revisar un lote grande de órdenes.
  const getOrdenesOrdenadas = useMemo(() => {
    const r = [...getOrdenesFiltradas];
    const dirMul = ordenColumna.dir === 'asc' ? 1 : -1;
    r.sort((a, b) => {
      if (ordenColumna.campo === 'tecnico') return dirMul * (a.tecnicoNombre || '').localeCompare(b.tecnicoNombre || '');
      if (ordenColumna.campo === 'referencia') return dirMul * String(a['full-reference'] || a.id).localeCompare(String(b['full-reference'] || b.id));
      if (ordenColumna.campo === 'validacion') {
        const val = o => { const p = ordenesPreValidadas.find(x => x.orden.id === o.id); return p ? (p.valida ? 2 : 0) : 1; };
        return dirMul * (val(a) - val(b));
      }
      // fecha (por defecto)
      const fa = new Date(a.dateOriginal || a.date).getTime();
      const fb = new Date(b.dateOriginal || b.date).getTime();
      return dirMul * (fa - fb);
    });
    return r;
  }, [getOrdenesFiltradas, ordenColumna, ordenesPreValidadas]);

  const toggleOrdenColumna = (campo) => setOrdenColumna(prev => prev.campo === campo ? { campo, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { campo, dir: 'asc' });

  const resetearFiltros  = () => setFiltros({ tecnico:'todos', periodo:'todos', fechaInicio:'', fechaFin:'', estadoValidacion:'todos', tipoError:'todos', busqueda:'', firmada:'todos' });
  const actualizarFiltro = (k, v) => setFiltros(prev => ({ ...prev, [k]: v }));
  const contarFiltrosActivos = () => ['tecnico','periodo','estadoValidacion','tipoError','firmada'].filter(k => filtros[k] !== 'todos').length + (filtros.busqueda.trim() ? 1 : 0);
  const getTecnicosUnicos = () => [...new Set(ordenes.map(o => o.tecnicoNombre))].sort();
  const getTiposError = () => { const s = new Set(); ordenesPreValidadas.forEach(v => obtenerTiposError(v).forEach(t => s.add(t))); return [...s]; };

  // ── Keyboard ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (!modalClienteAbierto) return;
      if (e.key === 'Escape')      cerrarModalCliente();
      if (e.key === 'ArrowLeft')  { e.preventDefault(); navegarOrden('anterior'); }
      if (e.key === 'ArrowRight') { e.preventDefault(); navegarOrden('siguiente'); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [modalClienteAbierto, ordenSeleccionadaModal]); // eslint-disable-line

  // ── Sub-componentes UI ─────────────────────────────────────────────────────
  // Chips de filtro activo, eliminables individualmente — mismo patrón que
  // MantenimientosView, para que se vea de un vistazo QUÉ filtros están
  // aplicados (antes solo había un contador agregado).
  const FILTRO_DEFAULT = { tecnico:'todos', periodo:'todos', estadoValidacion:'todos', tipoError:'todos', firmada:'todos' };
  const quitarFiltro = (k) => actualizarFiltro(k, FILTRO_DEFAULT[k] ?? 'todos');
  const chipsActivos = () => {
    const chips = [];
    if (filtros.tecnico !== 'todos') chips.push({ k: 'tecnico', label: filtros.tecnico });
    if (filtros.periodo !== 'todos') {
      const nombres = { hoy:'Hoy', semana:'Esta semana', mes:'Este mes', 'ultimo-mes':'Últimos 30 días', personalizado: `${filtros.fechaInicio||'…'} → ${filtros.fechaFin||'…'}` };
      chips.push({ k: 'periodo', label: nombres[filtros.periodo] || filtros.periodo });
    }
    if (filtros.estadoValidacion !== 'todos') chips.push({ k: 'estadoValidacion', label: { validas:'✅ Válidas', invalidas:'❌ Inválidas', 'sin-validar':'⏳ Sin validar' }[filtros.estadoValidacion] });
    if (filtros.tipoError !== 'todos') chips.push({ k: 'tipoError', label: NOMBRES_ERROR[filtros.tipoError] || filtros.tipoError });
    if (filtros.firmada !== 'todos') chips.push({ k: 'firmada', label: filtros.firmada === 'si' ? 'Firmada' : 'Sin firma' });
    if (filtros.busqueda.trim()) chips.push({ k: 'busqueda', label: `"${filtros.busqueda.trim()}"` });
    return chips;
  };

  const PanelFiltros = () => {
    const fa = contarFiltrosActivos();
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 sm:p-5 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Icons.Search className="w-4 h-4 text-gray-500" />
            <h3 className="font-semibold text-gray-800">Filtros</h3>
            {fa > 0 && <span className="bg-red-600 text-white px-2 py-0.5 rounded-full text-xs font-bold">{fa}</span>}
          </div>
          <div className="flex gap-3 text-sm">
            {fa > 0 && <button onClick={resetearFiltros} className="text-red-600 hover:text-red-700 font-semibold">✕ Limpiar todo</button>}
            <button onClick={() => setMostrarFiltrosAvanzados(p => !p)} className="text-gray-600 hover:text-gray-800 flex items-center gap-1">
              <Icons.ChevronDown className={`w-3.5 h-3.5 transition-transform ${mostrarFiltrosAvanzados ? 'rotate-180' : ''}`} /> Avanzados
            </button>
          </div>
        </div>

        {/* Chips de filtros activos — cada uno se puede quitar individualmente */}
        {fa > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {chipsActivos().map(c => (
              <span key={c.k} className="flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 rounded-xl px-2.5 py-1 text-xs font-bold">
                {c.label}
                <button onClick={() => quitarFiltro(c.k)} className="text-red-400 hover:text-red-700 ml-0.5 leading-none">✕</button>
              </span>
            ))}
          </div>
        )}

        {/* Presets rápidos de período */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[{l:'Hoy',p:'hoy'},{l:'Esta semana',p:'semana'},{l:'Este mes',p:'mes'},{l:'Últimos 30 días',p:'ultimo-mes'}].map(({l,p}) => (
            <button key={p} onClick={() => actualizarFiltro('periodo', filtros.periodo === p ? 'todos' : p)} disabled={exportando}
              className={`px-3 py-1 rounded-xl text-xs font-semibold border transition-all disabled:opacity-40 ${filtros.periodo===p ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-300 hover:border-red-400 hover:text-red-600'}`}>
              {l}
            </button>
          ))}
          {ordenesPreValidadas.length > 0 && [{l:'✅ Válidas',v:'validas'},{l:'❌ Inválidas',v:'invalidas'},{l:'⏳ Sin validar',v:'sin-validar'}].map(({l,v}) => (
            <button key={v} onClick={() => actualizarFiltro('estadoValidacion', filtros.estadoValidacion===v ? 'todos' : v)} disabled={exportando}
              className={`px-3 py-1 rounded-xl text-xs font-semibold border transition-all disabled:opacity-40 ${filtros.estadoValidacion===v ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-300 hover:border-red-400 hover:text-red-600'}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1"><Icons.Users className="w-3.5 h-3.5" />Técnico</label>
            <select value={filtros.tecnico} onChange={e => actualizarFiltro('tecnico', e.target.value)} disabled={exportando}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-red-400 focus:outline-none bg-white">
              <option value="todos">Todos ({ordenes.length})</option>
              {getTecnicosUnicos().map(t => <option key={t} value={t}>{t} ({ordenes.filter(o=>o.tecnicoNombre===t).length})</option>)}
            </select>
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1"><Icons.Calendar className="w-3.5 h-3.5" />Período</label>
            <select value={filtros.periodo} onChange={e => actualizarFiltro('periodo', e.target.value)} disabled={exportando}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-red-400 focus:outline-none bg-white">
              <option value="todos">Todas las fechas</option>
              <option value="hoy">Hoy</option>
              <option value="semana">Esta semana</option>
              <option value="mes">Este mes</option>
              <option value="ultimo-mes">Últimos 30 días</option>
              <option value="personalizado">Personalizado...</option>
            </select>
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1"><Icons.Search className="w-3.5 h-3.5" />Buscar</label>
            <input type="text" value={filtros.busqueda} onChange={e => actualizarFiltro('busqueda', e.target.value)}
              placeholder="Referencia, cliente..." disabled={exportando}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-red-400 focus:outline-none bg-white" />
          </div>
        </div>

        {filtros.periodo === 'personalizado' && (
          <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-red-50 rounded-xl border border-red-200">
            <div>
              <label className="block text-xs font-semibold text-red-700 mb-1">Desde</label>
              <input type="date" value={filtros.fechaInicio} onChange={e => actualizarFiltro('fechaInicio', e.target.value)}
                className="w-full border border-red-200 rounded-xl px-3 py-2 text-sm bg-white" disabled={exportando} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-red-700 mb-1">Hasta</label>
              <input type="date" value={filtros.fechaFin} onChange={e => actualizarFiltro('fechaFin', e.target.value)}
                className="w-full border border-red-200 rounded-xl px-3 py-2 text-sm bg-white" disabled={exportando} />
            </div>
          </div>
        )}

        {mostrarFiltrosAvanzados && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-gray-200">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1"><Icons.Check className="w-3.5 h-3.5" />Estado validación</label>
              <select value={filtros.estadoValidacion} onChange={e => actualizarFiltro('estadoValidacion', e.target.value)}
                disabled={exportando||!ordenesPreValidadas.length}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-red-400 focus:outline-none bg-white">
                <option value="todos">Todas</option>
                <option value="validas">✅ Válidas ({ordenesPreValidadas.filter(r=>r.valida).length})</option>
                <option value="invalidas">❌ Inválidas ({ordenesPreValidadas.filter(r=>!r.valida).length})</option>
                <option value="sin-validar">⏳ Sin validar ({ordenes.length - ordenesPreValidadas.length})</option>
              </select>
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1"><Icons.Tag className="w-3.5 h-3.5" />Tipo de error</label>
              <select value={filtros.tipoError} onChange={e => actualizarFiltro('tipoError', e.target.value)}
                disabled={exportando||!getTiposError().length}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-red-400 focus:outline-none bg-white">
                <option value="todos">Todos</option>
                {getTiposError().map(t => <option key={t} value={t}>{NOMBRES_ERROR[t] || t}</option>)}
              </select>
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1"><Icons.Edit className="w-3.5 h-3.5" />Firmada</label>
              <select value={filtros.firmada} onChange={e => actualizarFiltro('firmada', e.target.value)}
                disabled={exportando}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-red-400 focus:outline-none bg-white">
                <option value="todos">Todas</option>
                <option value="si">✅ Sí ({ordenes.filter(o=>o.signed).length})</option>
                <option value="no">❌ No ({ordenes.filter(o=>!o.signed).length})</option>
              </select>
            </div>
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-gray-200 text-sm text-gray-600">
          Mostrando <strong>{getOrdenesFiltradas.length}</strong> de <strong>{ordenes.length}</strong> órdenes
          {fa > 0 && <span className="ml-3 text-red-600">({fa} filtro{fa!==1?'s':''} activo{fa!==1?'s':''})</span>}
        </div>
      </div>
    );
  };

  // Modal de progreso/confirm/done
  const LiqModal = () => {
    if (!liqModal) return null;
    const { type, title, message, detail, stats, items, hasErrors } = liqModal;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
          <div className="border-b border-gray-100 px-5 py-4">
            <h3 className="font-bold text-base text-gray-900">{title}</h3>
            {message && <p className="text-gray-500 text-sm mt-1">{message}</p>}
          </div>
          <div className="p-5">
            {type === 'confirm' && (
              <>
                {detail && <p className="text-sm text-gray-600 mb-5">{detail}</p>}
                <div className="flex gap-3">
                  <button onClick={cancelLiq} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50">Cancelar</button>
                  <button onClick={confirmLiq} className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700">Confirmar</button>
                </div>
              </>
            )}
            {type === 'progress' && (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600 flex-shrink-0" />
                  <span className="text-sm text-gray-600">{liqModal.processed} / {liqModal.total}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
                  <div className="h-2 rounded-full bg-red-500 transition-all duration-300" style={{ width: liqModal.total > 0 ? `${(liqModal.processed/liqModal.total)*100}%` : '0%' }} />
                </div>
                {items?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto border rounded-xl">
                    {items.map((it,i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 border-b last:border-0 text-xs">
                        <span className={it.status==='ok' ? 'text-green-500' : 'text-red-500'}>{it.status==='ok' ? '✓' : '✗'}</span>
                        <span className="font-mono flex-1 truncate">{it.name}</span>
                        {it.detail && <span className="text-gray-400 truncate max-w-[120px]">{it.detail}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {type === 'info' && (
              <>
                <p className="text-sm text-gray-600 mb-5">{message}</p>
                <button onClick={() => setLiqModal(null)} className="w-full py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200">Cerrar</button>
              </>
            )}
            {type === 'done' && (
              <>
                {stats && (
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {stats.validas   !== undefined && <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center"><div className="text-2xl font-bold text-green-700">{stats.validas}</div><div className="text-xs text-green-600">Válidas</div></div>}
                    {stats.invalidas !== undefined && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center"><div className="text-2xl font-bold text-red-700">{stats.invalidas}</div><div className="text-xs text-red-600">Inválidas</div></div>}
                    {stats.exitosas  !== undefined && <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center"><div className="text-2xl font-bold text-green-700">{stats.exitosas}</div><div className="text-xs text-green-600">Exportadas</div></div>}
                    {stats.errores   !== undefined && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center"><div className="text-2xl font-bold text-red-700">{stats.errores}</div><div className="text-xs text-red-600">Con error</div></div>}
                  </div>
                )}
                {stats?.nombre && <p className="text-xs text-gray-500 mb-3 text-center flex items-center justify-center gap-1"><Icons.FileText className="w-3.5 h-3.5" />{stats.nombre} {stats.secs ? `· ${stats.secs}s` : ''}</p>}
                {items?.length > 0 && (
                  <div className="max-h-36 overflow-y-auto border rounded-xl mb-4">
                    {items.map((it,i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 border-b last:border-0 text-xs">
                        <span className="text-red-500">✗</span>
                        <span className="font-mono flex-shrink-0">{it.name}</span>
                        <span className="text-gray-400 truncate">{it.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  {hasErrors && (
                    <button onClick={() => { exportarErroresACSV(); setLiqModal(null); }}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50">
                      <Icons.Download className="w-4 h-4" /> Descargar errores
                    </button>
                  )}
                  <button onClick={() => setLiqModal(null)} className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700">Cerrar</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Panel lateral de detalle (antes era un modal centrado) — el propio flujo
  // ya tenía navegación ← → con teclado para pasar de una orden a la
  // siguiente, así que un drawer que no tapa la lista de fondo (como el
  // SidePanel de MantenimientosView) encaja mucho mejor que un modal que
  // bloquea toda la pantalla en cada revisión.
  const DetalleDrawer = () => {
    if (!modalClienteAbierto || !ordenSeleccionadaModal) return null;
    const lista  = getOrdenesOrdenadas;
    const idx    = lista.findIndex(o => o.id === ordenSeleccionadaModal.id);
    const ap     = ordenSeleccionadaModal['account-path'];
    const info   = clientesCache[ap];
    const preVal = ordenesPreValidadas.find(r => r.orden.id === ordenSeleccionadaModal.id);
    const serialNorm = assetModal?.serialNumber ? normalizarNumeroSerie(assetModal.serialNumber) : null;

    return (
      <>
        <div className="fixed inset-0 z-40 bg-black/20" onClick={cerrarModalCliente} />
        <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-[600px] bg-white shadow-2xl flex flex-col border-l overflow-hidden">

          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="bg-red-100 text-red-800 font-mono font-bold px-3 py-1 rounded-lg text-sm">
                    {ordenSeleccionadaModal['full-reference'] || `#${ordenSeleccionadaModal.id}`}
                  </span>
                  {ordenSeleccionadaModal.signed
                    ? <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">✓ Firmada</span>
                    : <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">✗ Sin firma</span>}
                  {preVal?.valida && <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">✅ Válida</span>}
                  {preVal && !preVal.valida && <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">❌ Inválida</span>}
                  <span className="text-xs text-gray-400">{idx+1}/{lista.length}</span>
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-3">
                  <span className="flex items-center gap-1"><Icons.Calendar className="w-3.5 h-3.5" />{new Date(ordenSeleccionadaModal.dateOriginal || ordenSeleccionadaModal.date).toLocaleDateString('es-ES')}</span>
                  <span className="flex items-center gap-1"><Icons.Users className="w-3.5 h-3.5" />{ordenSeleccionadaModal.tecnicoNombre}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => navegarOrden('anterior')} disabled={idx <= 0} className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-30" title="Orden anterior (←)">
                  <Icons.ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => navegarOrden('siguiente')} disabled={idx >= lista.length-1} className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-30" title="Orden siguiente (→)">
                  <Icons.ChevronRight className="w-5 h-5" />
                </button>
                <button onClick={cerrarModalCliente} className="p-1.5 hover:bg-gray-100 rounded-lg ml-1 text-gray-400 hover:text-gray-600">
                  <Icons.X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-5">

            {/* Activo del equipo */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                <Icons.Wrench className="w-4 h-4" /> Equipo / Activo
                {loadingAssetModal && <div className="animate-spin h-4 w-4 border-b-2 border-red-600 rounded-full" />}
              </h4>
              {!ordenSeleccionadaModal.assets?.length ? (
                <p className="text-red-600 text-sm italic">Sin activos registrados en esta orden</p>
              ) : assetModal?.error ? (
                <p className="text-red-600 text-sm">{assetModal.error}</p>
              ) : assetModal ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Marca</p>
                    <p className="font-bold text-gray-900">{assetModal.brand || '—'}</p>
                    {assetModal.brand && !esMarcaValida(assetModal.brand) && (
                      <p className="text-xs text-red-600 mt-0.5">⚠ No válida para Ariston</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Nº Serie</p>
                    <p className="font-bold text-gray-900 font-mono text-xs">{assetModal.serialNumber || '—'}</p>
                    {serialNorm && serialNorm !== assetModal.serialNumber && (
                      <p className="text-xs text-amber-600 mt-0.5">→ {serialNorm}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Garantía desde</p>
                    <p className="font-bold text-gray-900">{assetModal.warrantyStartDate ? formatearFecha(assetModal.warrantyStartDate) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Modelo</p>
                    <p className="font-bold text-gray-900 text-xs truncate">{assetModal.model || assetModal.name || '—'}</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 text-sm italic">Cargando activo...</p>
              )}
            </div>

            {/* Validación individual */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2"><Icons.Search className="w-4 h-4" />Validación individual</h4>
                <button
                  onClick={validarOrdenIndividual}
                  disabled={validandoModal}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                >
                  {validandoModal ? <><div className="animate-spin h-3 w-3 border-b-2 border-white rounded-full" />Validando...</> : 'Validar ahora'}
                </button>
              </div>

              {preVal && !validacionModal && (
                <div className={`p-3 rounded-lg text-sm ${preVal.valida ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {preVal.valida
                    ? <span className="text-green-700">✅ Orden válida para exportar</span>
                    : <div className="text-red-700"><strong>Motivos:</strong> {preVal.errores.join(' · ')}</div>}
                </div>
              )}

              {validacionModal && (
                <div className={`p-3 rounded-lg text-sm ${validacionModal.valida ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {validacionModal.valida
                    ? <span className="text-green-700">✅ Válida</span>
                    : <div className="text-red-700"><strong>Errores:</strong> {validacionModal.errores.join(' · ')}</div>}
                </div>
              )}

              {!preVal && !validacionModal && !validandoModal && (
                <p className="text-gray-400 text-sm italic">Pulsa "Validar ahora" para comprobar esta orden individualmente</p>
              )}
            </div>

            {/* Cliente */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Icons.Users className="w-4 h-4" />Cliente</h4>
              {!ap ? <p className="text-gray-500 text-sm italic">Sin cuenta asociada</p>
                : loadingClientes[ap] ? <p className="text-gray-500 text-sm">Cargando...</p>
                : info?.error ? <p className="text-red-600 text-sm">{info.error}</p>
                : info ? (
                  <div className="space-y-3">
                    <p className="text-lg font-bold text-gray-900">{info.displayName}</p>
                    <div className="grid grid-cols-1 gap-3 text-sm">
                      <div className="bg-gray-50 rounded-xl p-3 border">
                        <p className="text-xs font-semibold text-gray-500 mb-1">Teléfono</p>
                        <p className="font-medium">{info.contacto.telefono1 || '—'}</p>
                        {info.contacto.telefono2 && <p className="text-xs text-gray-500 mt-0.5">{info.contacto.telefono2}</p>}
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 border">
                        <p className="text-xs font-semibold text-gray-500 mb-1">Email</p>
                        <p className="font-medium text-xs break-all">{info.contacto.email || '—'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 border">
                        <p className="text-xs font-semibold text-gray-500 mb-1">Dirección</p>
                        <p className="font-medium text-xs">{info.direccionFormateada || '—'}</p>
                      </div>
                    </div>
                  </div>
                ) : <p className="text-gray-400 text-sm italic">Cargando...</p>}
            </div>

            {/* Comentarios */}
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-2"><Icons.FileText className="w-4 h-4" />Descripción / Avería</h4>
                <div className="bg-gray-50 rounded-xl border p-3 text-xs text-gray-700 whitespace-pre-wrap max-h-28 overflow-y-auto">
                  {ordenSeleccionadaModal['private-comments'] || 'Sin descripción'}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-2"><Icons.MessageCircle className="w-4 h-4" />Comentario técnico</h4>
                <div className="bg-blue-50 rounded-xl border border-blue-100 p-3 text-xs text-blue-900 whitespace-pre-wrap max-h-28 overflow-y-auto">
                  {ordenSeleccionadaModal.comments?.trim() || 'Sin comentario'}
                </div>
              </div>
            </div>

            {/* Piezas */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-2"><Icons.Package className="w-4 h-4" />Líneas / Piezas</h4>
              {(() => {
                const productos = getProductosOrden(ordenSeleccionadaModal);
                if (!productos.length) return <p className="text-gray-400 text-sm italic">Sin piezas registradas</p>;
                return (
                  <div className="grid grid-cols-2 gap-2">
                    {productos.map((p, i) => (
                      <div key={p.id||i} className="bg-green-50 rounded-xl border border-green-200 p-3 text-xs">
                        <p className="font-semibold text-green-800 truncate">{p.name}</p>
                        <p className="text-gray-500 font-mono mt-0.5">{p.reference || '—'} · ×{p.quantity}</p>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="border-t border-gray-100 px-5 py-3 flex justify-end flex-shrink-0">
            <button onClick={cerrarModalCliente} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-xl font-medium text-sm transition">
              Cerrar
            </button>
          </div>
        </div>
      </>
    );
  };

  // ── Render principal ───────────────────────────────────────────────────────
  const filtrosActivos = contarFiltrosActivos();
  const validas   = ordenesPreValidadas.filter(r=>r.valida).length;
  const invalidas = ordenesPreValidadas.filter(r=>!r.valida).length;
  const pctFirmadas = ordenes.length > 0 ? Math.round((ordenes.filter(o=>o.signed).length / ordenes.length) * 100) : 0;

  // Una orden con varios errores cuenta en cada tipo que tenga — la suma de
  // este desglose puede superar el nº de inválidas, y es correcto: es la
  // cuenta de "cuántas órdenes tienen (también) este problema".
  const desglose = ordenesPreValidadas.length > 0
    ? Object.entries(
        ordenesPreValidadas.filter(r=>!r.valida).reduce((acc, r) => {
          obtenerTiposError(r).forEach(t => { acc[t] = (acc[t]||0) + 1; });
          return acc;
        }, {})
      ).sort((a,b) => b[1]-a[1])
    : [];

  // ── Modal Importar Errores Ariston ────────────────────────────────────────
  const ImportarErroresModal = () => {
    if (!importarErrores.open) return null;
    const { step, filas, resultados, parseError, procesando } = importarErrores;
    const conOrden = filas.filter(f => f.orden);
    const sinOrden = filas.filter(f => !f.orden);
    const exitosos = resultados.filter(r => r.success);
    const fallidos = resultados.filter(r => !r.success);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

          {/* Header */}
          <div className="border-b border-gray-100 px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                <Icons.FileText className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-base text-gray-900">Importar errores Ariston · Ya existe</h3>
                <p className="text-gray-500 text-xs mt-0.5">Crea albarán y cierra la orden para cada "Ya existe un parte"</p>
              </div>
            </div>
            {!procesando && (
              <button onClick={() => setImportarErrores(prev => ({ ...prev, open: false }))}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition text-gray-400 hover:text-gray-600">
                <Icons.X className="w-5 h-5" />
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">

            {/* Step: upload */}
            {(step === 'upload' || step === 'preview') && (
              <div
                className="border-2 border-dashed border-orange-300 rounded-xl p-8 text-center cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition"
                onClick={() => importarErroresFileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); parsearArchivoErrores(e.dataTransfer.files[0]); }}>
                <Icons.FileText className="w-8 h-8 mx-auto mb-2 text-orange-400" />
                <p className="text-sm font-semibold text-gray-700">Arrastra el CSV de errores Ariston o haz clic para seleccionar</p>
                <p className="text-xs text-gray-400 mt-1">Detecta filas con "Ya existe un parte"</p>
                <input ref={importarErroresFileRef} type="file" accept=".csv,.txt" className="hidden"
                  onChange={e => parsearArchivoErrores(e.target.files[0])} />
              </div>
            )}

            {parseError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{parseError}</div>
            )}

            {/* Step: preview */}
            {step === 'preview' && filas.length > 0 && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-orange-700">{filas.length}</div>
                    <div className="text-xs text-orange-600">Ya existen</div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-green-700">{conOrden.length}</div>
                    <div className="text-xs text-green-600">Encontradas</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-gray-500">{sinOrden.length}</div>
                    <div className="text-xs text-gray-500">No encontradas</div>
                  </div>
                </div>

                <div className="border rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 border-b">
                    Órdenes a procesar ({conOrden.length})
                  </div>
                  <div className="max-h-52 overflow-y-auto divide-y">
                    {conOrden.map((f, i) => {
                      const tipo = obtenerTipoActividad(f.orden.lines, f.orden['private-comments']);
                      return (
                        <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                          <span className="font-mono font-semibold text-gray-800 w-24 flex-shrink-0">{f.referencia}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${tipo==='1'?'bg-blue-100 text-blue-700':tipo==='7'?'bg-purple-100 text-purple-700':'bg-green-100 text-green-700'}`}>
                            {tipo==='1'?'PM':tipo==='2'?'Gnt.Ap':tipo==='6'?'Gnt.Pz':tipo==='7'?'Sust':'?'}
                          </span>
                          <span className="text-xs text-gray-500 truncate flex-1">{f.orden.tecnicoNombre}</span>
                        </div>
                      );
                    })}
                    {conOrden.length === 0 && <div className="px-3 py-4 text-sm text-gray-400 text-center">Ninguna orden encontrada en la vista actual</div>}
                  </div>
                </div>

                {sinOrden.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-xs font-semibold text-amber-700 mb-1">No encontradas en la lista cargada ({sinOrden.length})</p>
                    <p className="text-xs text-amber-600 flex flex-wrap gap-1">
                      {sinOrden.map((f, i) => <span key={i} className="font-mono bg-white border border-amber-200 px-1.5 py-0.5 rounded">{f.referencia}</span>)}
                    </p>
                  </div>
                )}

                {conOrden.length > 0 && (
                  <button onClick={procesarErroresAriston}
                    className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition">
                    Crear {conOrden.length} albarán{conOrden.length !== 1 ? 'es' : ''} y cerrar órdenes
                  </button>
                )}
              </>
            )}

            {/* Step: processing */}
            {step === 'processing' && (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto mb-3" />
                <p className="text-sm font-semibold text-gray-700">Procesando… {resultados.length} / {conOrden.length}</p>
                <div className="w-full bg-gray-100 rounded-full h-2 mt-3">
                  <div className="h-2 rounded-full bg-orange-500 transition-all duration-300"
                    style={{ width: conOrden.length > 0 ? `${(resultados.length / conOrden.length) * 100}%` : '0%' }} />
                </div>
                {resultados.length > 0 && (
                  <div className="mt-4 border rounded-xl overflow-hidden max-h-48 overflow-y-auto divide-y text-left">
                    {resultados.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                        <span className={r.success ? 'text-green-500' : 'text-red-500'}>{r.success ? '✓' : '✗'}</span>
                        <span className="font-mono font-semibold w-20 flex-shrink-0">{r.referencia || r.fullReference}</span>
                        {r.success
                          ? <span className="text-green-600">{r.albaranRef || `ID ${r.albaranId}`} · {r.closed ? 'Cerrada' : 'Cierre pendiente'}</span>
                          : <span className="text-red-500 truncate">{r.error}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step: done */}
            {step === 'done' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-green-700">{exitosos.length}</div>
                    <div className="text-xs text-green-600 mt-1">Albaranes creados</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-red-700">{fallidos.length}</div>
                    <div className="text-xs text-red-600 mt-1">Con error</div>
                  </div>
                </div>

                <div className="border rounded-xl overflow-hidden max-h-60 overflow-y-auto divide-y">
                  {resultados.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 text-xs">
                      <span className={r.success ? 'text-green-500' : 'text-red-500'}>{r.success ? '✓' : '✗'}</span>
                      <span className="font-mono font-semibold w-24 flex-shrink-0">{r.referencia || r.fullReference}</span>
                      {r.success
                        ? <span className="text-green-600">{r.albaranRef || `ID ${r.albaranId}`} · {r.closed ? '✓ Cerrada' : '⚠ Cierre pendiente'}</span>
                        : <span className="text-red-500 truncate">{r.error}</span>}
                    </div>
                  ))}
                </div>

                <button onClick={() => setImportarErrores({ open: false, step: 'upload', filas: [], resultados: [], parseError: '', procesando: false })}
                  className="w-full py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition">
                  Cerrar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Icons.DollarSign className="w-7 h-7 text-red-600" />
              Pre-liquidación Ariston
              {(loading||exportando) && <div className="animate-spin h-5 w-5 border-b-2 border-red-600 rounded-full" />}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Pre-validación y exportación CSV · {ordenes.length} órdenes cargadas
              {lastUpdated && <span className="ml-2 text-xs">· Actualizado {lastUpdated.toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit'})}</span>}
              {rangoActivo && <span className="ml-2 text-xs text-red-600 font-semibold">· Rango {rangoActivo.desde || '…'} → {rangoActivo.hasta || '…'}</span>}
            </p>
            {(antiguedad.mas12 > 0 || mostrarTodasAntiguas) && (
              <div className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 inline-flex items-center gap-2 flex-wrap">
                <span>
                  {mostrarTodasAntiguas
                    ? <>Mostrando <strong>todas</strong>, incluidas {antiguedad.mas12} de más de 12 meses (probable drift sin sincronizar).</>
                    : <><strong>{antiguedad.ocultadas}</strong> órdenes de más de 12 meses ocultas — casi siempre son liquidaciones antiguas que ya salieron del estado en StelOrder.</>}
                </span>
                <button onClick={() => setMostrarTodasAntiguas(v => !v)} disabled={loading}
                  className="font-semibold text-amber-900 underline hover:no-underline disabled:opacity-40">
                  {mostrarTodasAntiguas ? 'Ocultar las antiguas' : 'Ver todas'}
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            {/* Flujo principal: solo Exportar. El paso "Pre-validar" ya no
                existe — las órdenes llegan validadas al cargar
                (getAristonLiquidaciones.js). */}
            <div className="flex items-center gap-1.5">
              <button onClick={exportarAExcel}
                disabled={exportando||loading||!getOrdenesFiltradas.length}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition">
                <Icons.Download className="w-4 h-4" />
                {exportando ? <><div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full"/>{progresoExportacion.actual}/{progresoExportacion.total}</> : 'Exportar CSV'}
              </button>
            </div>

            {/* Herramientas — utilidades, no forman parte del flujo principal */}
            <div className="flex flex-wrap justify-end gap-2">
              <button onClick={() => setImportarErrores(prev => ({ ...prev, open: true, step: 'upload', filas: [], resultados: [], parseError: '' }))}
                disabled={loading||exportando||!ordenes.length}
                className="flex items-center gap-2 bg-orange-50 hover:bg-orange-100 border border-orange-200 disabled:opacity-40 text-orange-700 px-3 py-1.5 rounded-xl text-xs font-semibold transition">
                <Icons.FileText className="w-3.5 h-3.5" /> Importar errores
              </button>

              {filtros.periodo !== 'todos' && (
                <button onClick={cargarPorRangoSeleccionado} disabled={loading||exportando}
                  title="Pide a StelOrder solo las órdenes del período marcado, en vez de las 500 completas"
                  className="flex items-center gap-2 bg-white border border-red-200 hover:bg-red-50 disabled:opacity-40 text-red-600 px-3 py-1.5 rounded-xl text-xs font-semibold transition">
                  <Icons.Refresh className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'Cargando...' : 'Cargar solo este rango'}
                </button>
              )}

              <button onClick={() => cargarOrdenes()} disabled={loading||exportando}
                className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-gray-600 px-3 py-1.5 rounded-xl text-xs font-semibold transition">
                <Icons.Refresh className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Cargando...' : 'Recargar'}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl mb-5 text-sm flex items-center gap-2">
            <Icons.X className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}

        {/* Panel estadísticas — clicables, aplican el filtro correspondiente
            (antes solo las pastillas de abajo del panel de filtros lo hacían;
            estas tarjetas mostraban los mismos datos pero sin poder pulsarlas) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <KPICard icon={<Icons.FileText className="w-4 h-4" />} colorBg="bg-gray-100" colorIcon="text-gray-500"
            label="Total cargadas" value={ordenes.length} sub={`${getOrdenesFiltradas.length} filtradas`}
            onClick={filtrosActivos > 0 ? resetearFiltros : undefined} />
          <KPICard icon={<Icons.Edit className="w-4 h-4" />} colorBg="bg-blue-100" colorIcon="text-blue-600"
            label="Firmadas" value={ordenes.filter(o=>o.signed).length} sub={`${pctFirmadas}%`}
            onClick={() => actualizarFiltro('firmada', filtros.firmada==='si' ? 'todos' : 'si')} />
          {ordenesPreValidadas.length > 0 ? (
            <>
              <KPICard icon={<Icons.Check className="w-4 h-4" />} colorBg="bg-green-100" colorIcon="text-green-600"
                label="Válidas" value={validas} sub={`${ordenesPreValidadas.length>0 ? Math.round((validas/ordenesPreValidadas.length)*100) : 0}% del total`}
                onClick={() => actualizarFiltro('estadoValidacion', filtros.estadoValidacion==='validas' ? 'todos' : 'validas')} />
              <KPICard icon={<Icons.X className="w-4 h-4" />} colorBg="bg-red-100" colorIcon="text-red-600" alert={invalidas > 0}
                label="Inválidas" value={invalidas} sub={`${ordenesPreValidadas.length>0 ? Math.round((invalidas/ordenesPreValidadas.length)*100) : 0}% del total`}
                onClick={() => actualizarFiltro('estadoValidacion', filtros.estadoValidacion==='invalidas' ? 'todos' : 'invalidas')} />
            </>
          ) : (
            Object.entries(estadisticas.porTecnico || {}).sort(([,a],[,b])=>b-a).slice(0,2).map(([tec,count]) => (
              <button key={tec} onClick={() => actualizarFiltro('tecnico', filtros.tecnico===tec ? 'todos' : tec)}
                className="text-left bg-white rounded-2xl border border-gray-200 shadow-sm p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: tecnicoColors[tec]||'#6B7280'}} />
                  <p className="text-xs font-semibold text-gray-500 uppercase">{tec}</p>
                </div>
                <p className="text-3xl font-bold text-gray-900">{count}</p>
                <p className="text-xs text-gray-400 mt-1">{ordenes.length>0 ? ((count/ordenes.length)*100).toFixed(0) : 0}% del total</p>
              </button>
            ))
          )}
        </div>

        {/* Desglose de errores post-validación */}
        {desglose.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <h4 className="font-semibold text-red-800 text-sm mb-3 flex items-center gap-2"><Icons.Tag className="w-4 h-4" />Desglose de errores ({invalidas} inválidas)</h4>
            <div className="flex flex-wrap gap-2">
              {desglose.map(([tipo, count]) => (
                <button key={tipo} onClick={() => actualizarFiltro('tipoError', filtros.tipoError===tipo ? 'todos' : tipo)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${filtros.tipoError===tipo ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-200 hover:border-red-400'}`}>
                  {NOMBRES_ERROR[tipo] || tipo}
                  <span className={`px-1.5 py-0.5 rounded-full ${filtros.tipoError===tipo ? 'bg-red-800' : 'bg-red-100'}`}>{count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Errores de la última exportación */}
        {validacionesExportacion.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-amber-800 text-sm">⚠️ {validacionesExportacion.length} órdenes fallaron en la última exportación</h4>
              <button onClick={exportarErroresACSV} className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900 bg-white border border-amber-300 px-3 py-1.5 rounded-lg transition">
                <Icons.Download className="w-3.5 h-3.5" /> Descargar CSV de errores
              </button>
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {validacionesExportacion.slice(0,10).map((e,i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-amber-800">
                  <span className="font-mono font-semibold flex-shrink-0">{e.fullReference}</span>
                  <span className="text-amber-600 truncate">— {e.error}</span>
                </div>
              ))}
              {validacionesExportacion.length > 10 && <p className="text-xs text-amber-500 italic">...y {validacionesExportacion.length - 10} más</p>}
            </div>
          </div>
        )}

        <PanelFiltros />

        {/* Lista de órdenes */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4" />
            <p className="text-gray-600">Cargando órdenes...</p>
          </div>
        ) : getOrdenesFiltradas.length === 0 ? (
          <div className="text-center py-16">
            <Icons.Search className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-20" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Sin resultados</h3>
            <p className="text-gray-500 mb-4">No se encontraron órdenes con los filtros aplicados</p>
            {filtrosActivos > 0 && (
              <button onClick={resetearFiltros} className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-medium">
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            {/* ── Tabla escritorio ── */}
            {/* Antes esto era una lista de tarjetas apiladas en cualquier tamaño
                de pantalla — para un gestor repasando cientos de órdenes, una
                tabla densa y ordenable por columna (como en Órdenes/Mantenimientos)
                deja ver muchas más filas por pantalla y agrupar por técnico o por
                fecha antes de liquidar. */}
            <div className="hidden sm:block bg-white rounded-2xl border border-gray-200 shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {[
                      { campo: 'referencia', label: 'Ref.' },
                      { campo: 'fecha', label: 'Fecha' },
                      { campo: 'tecnico', label: 'Técnico' },
                    ].map(col => (
                      <th key={col.campo} onClick={() => toggleOrdenColumna(col.campo)}
                        className="px-4 py-2.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {ordenColumna.campo === col.campo && <Icons.ChevronDown className={`w-3 h-3 transition-transform ${ordenColumna.dir === 'asc' ? 'rotate-180' : ''}`} />}
                        </span>
                      </th>
                    ))}
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Firmada</th>
                    <th onClick={() => toggleOrdenColumna('validacion')}
                      className="px-4 py-2.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        Validación
                        {ordenColumna.campo === 'validacion' && <Icons.ChevronDown className={`w-3 h-3 transition-transform ${ordenColumna.dir === 'asc' ? 'rotate-180' : ''}`} />}
                      </span>
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Motivo</th>
                    <th className="px-4 py-2.5 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {getOrdenesOrdenadas.map((ord) => {
                    const pv = ordenesPreValidadas.find(r => r.orden.id === ord.id);
                    return (
                      <tr key={ord.id} onClick={() => abrirModalCliente(ord)}
                        className={`cursor-pointer hover:bg-gray-50 transition-colors ${pv?.valida ? 'bg-green-50/40' : pv ? 'bg-red-50/40' : ''}`}>
                        <td className="px-4 py-2.5">
                          <span className="bg-red-100 text-red-800 font-mono font-bold px-2 py-0.5 rounded text-xs whitespace-nowrap">
                            {ord['full-reference'] || `#${ord.id}`}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap text-gray-700">{new Date(ord.dateOriginal || ord.date).toLocaleDateString('es-ES')}</td>
                        <td className="px-4 py-2.5 whitespace-nowrap text-gray-700">{ord.tecnicoNombre || '—'}</td>
                        <td className="px-4 py-2.5 max-w-[180px] truncate text-gray-700">{ord['customer-name'] || '—'}</td>
                        <td className="px-4 py-2.5">
                          {ord.signed
                            ? <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-[11px] font-semibold"><Icons.Check className="w-3 h-3" />Sí</span>
                            : <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-[11px] font-semibold">No</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          {pv?.valida && <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[11px] font-semibold"><Icons.Check className="w-3 h-3" />Válida</span>}
                          {pv && !pv.valida && <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-[11px] font-semibold"><Icons.X className="w-3 h-3" />Inválida</span>}
                          {!pv && <span className="bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full text-[11px] font-semibold">Sin validar</span>}
                        </td>
                        <td className="px-4 py-2.5 max-w-[260px] truncate text-xs text-red-600">{pv && !pv.valida ? pv.errores.join(' · ') : ''}</td>
                        <td className="px-4 py-2.5 text-gray-300">
                          <Icons.ChevronRight className="w-4 h-4" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Tarjetas móvil ── */}
            <div className="sm:hidden space-y-3">
              {getOrdenesOrdenadas.map((ord, i) => {
                const pv = ordenesPreValidadas.find(r => r.orden.id === ord.id);
                return (
                  <div key={ord.id} onClick={() => abrirModalCliente(ord)} className={`border rounded-xl p-4 transition-all cursor-pointer hover:shadow-md ${
                    pv?.valida ? 'bg-green-50 border-green-200 hover:border-green-300'
                    : pv ? 'bg-red-50 border-red-200 hover:border-red-300'
                    : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="bg-red-100 text-red-800 font-mono font-bold px-3 py-1 rounded-lg text-sm">
                          {ord['full-reference'] || `#${ord.id}`}
                        </span>
                        {ord.signed
                          ? <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">✓ Firmada</span>
                          : <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">Sin firma</span>}
                        {pv?.valida && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">✅ Válida</span>}
                        {pv && !pv.valida && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">❌ Inválida</span>}
                      </div>
                      <span className="text-xs text-gray-400">{i+1}/{getOrdenesOrdenadas.length}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Fecha</p>
                        <p className="font-medium">{new Date(ord.dateOriginal || ord.date).toLocaleDateString('es-ES')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Técnico</p>
                        <p className="font-medium">{ord.tecnicoNombre || '—'}</p>
                      </div>
                      {ord['customer-name'] && (
                        <div className="col-span-2">
                          <p className="text-xs text-gray-500 mb-0.5">Cliente</p>
                          <p className="font-medium truncate">{ord['customer-name']}</p>
                        </div>
                      )}
                    </div>

                    {(ord['private-comments'] || ord.comments) && (
                      <div className="mt-2 text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg space-y-1">
                        {ord['private-comments'] && (
                          <div>
                            <span className="text-gray-400 mr-1">Descripción:</span>
                            {ord['private-comments'].length > 120
                              ? ord['private-comments'].substring(0, 120).replace(/[\n\r]+/g, ' ') + '…'
                              : ord['private-comments'].replace(/[\n\r]+/g, ' ')}
                          </div>
                        )}
                        {ord.comments && (
                          <div>
                            <span className="text-gray-400 mr-1">Comentario técnico:</span>
                            {ord.comments.length > 120
                              ? ord.comments.substring(0, 120).replace(/[\n\r]+/g, ' ') + '…'
                              : ord.comments.replace(/[\n\r]+/g, ' ')}
                          </div>
                        )}
                      </div>
                    )}

                    {pv && !pv.valida && (
                      <div className="mt-3 text-xs text-red-700 bg-red-100 px-3 py-2 rounded-lg">
                        <strong>Motivo:</strong> {pv.errores.join(' · ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <DetalleDrawer />
        <LiqModal />
        <ImportarErroresModal />

        <div className="mt-10 text-center text-xs text-gray-400">
          Pre-validación automática al cargar · {ordenesPreValidadas.length} órdenes validadas
          {lastUpdated && <span className="ml-3">Última carga: {lastUpdated.toLocaleString('es-ES')}</span>}
        </div>
      </div>
    </div>
  );
};

export default LiquidacionesView;
